import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Card, Form, Row, Col, Image, Button } from 'react-bootstrap';

// Default theme colors
const argonTheme = {
	COLORS: {
		DEFAULT: '#32325d',
		INFO: '#17a2b8',
		BG_SIMULATOR: '#f8f9fa',
		WHITE: '#ffffff',
		BORDER: '#dee2e6',
	},
};

const CheckBoxInput = ({ data, handelChange, currentFormValue }) => {
	const [values, setValues] = useState([]);
	const [options, setOptions] = useState(data?.options || []);
	const [spinner, setSpinner] = useState(false);

	useEffect(() => {
		if (data?.options?.length) {
			setOptions(data.options);
			const checkedOptions = data.options.filter((option) => option.checked);

			if (checkedOptions.length > 0) {
				const checkedValues = checkedOptions.map((option) => option.value);
				setValues(checkedValues);
				handelChange({ [data.label]: checkedOptions });
			}
		} else {
			setSpinner(data?.id);
			const fetchFn = data?.fetchData;

			if (typeof fetchFn === 'function') {
				fetchFn('7aa64b90-50b7-4204-b98e-7b08bef88a4b', currentFormValue, (optionsData) => {
					setOptions(optionsData);
					setSpinner(false);
				});
			} else {
				console.warn('❌ fetchData not available on data:', data);
				setSpinner(false);
			}
		}
	}, [data, handelChange]);

	// inside CheckBoxInput component
	const orderRef = useRef({}); // keep order of first selection per option

	const handleCheckboxChange = (optionValue) => {
		const newValues = values.includes(optionValue)
			? values.filter((v) => v !== optionValue)
			: [...values, optionValue];

		setValues(newValues);

		const selectedOptions = options.filter((opt) => newValues.includes(opt.value));

		// assign a stable order per option (first time it is selected)
		selectedOptions.forEach((opt) => {
			if (orderRef.current[opt.value] == null) {
				orderRef.current[opt.value] = Date.now() + Math.random(); // stable enough
			}
		});

		const items = selectedOptions
			.map((opt) => ({
				groupId: data.id,
				groupLabel: data.label,
				value: opt.value,
				order: orderRef.current[opt.value],
				text: opt.description ? opt.description.trim() : `• ${opt.label ?? opt.value}`,
				price: Number(opt.lst_price) || 0,
			}))
			.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

		const descriptionText = items
			.map((i) => i.text)
			.filter(Boolean)
			.join('\n');
		const totalPrice = items.reduce((sum, i) => sum + (Number(i.price) || 0), 0);

		// ✅ UNIQUE KEYS (no overwriting between components)
		handelChange({
			[`${data.id}`]: descriptionText,
			[`${data.id}_price`]: totalPrice,
			[`${data.id}_items`]: items, // for global aggregation
		});
	};

	return (
		<Card
			className='mb-4 border-0'
			style={{
				backgroundColor: argonTheme.COLORS.BG_SIMULATOR,
				borderRadius: '15px',
				padding: '20px',
			}}>
			{data?.label && (
				<Card.Title
					className='mb-4'
					style={{
						color: argonTheme.COLORS.DEFAULT,
						fontSize: '1.1rem',
						fontWeight: '600',
					}}>
					{data.label}
					{data.required && <span className='text-danger ms-1'>*</span>}
				</Card.Title>
			)}

			<Card.Body className='p-0'>
				{!!options.length > 0 && !spinner ? (
					<Row className='g-3'>
						{options.map((option, idx) => {
							const isChecked = values.includes(option.value);
							const isDisabled = option.disabled;

							return (
								<Col key={idx} xs={12}>
									<Button
										variant='light'
										className={`d-flex align-items-center justify-content-start w-100 p-3 border ${
											isDisabled ? 'opacity-50' : ''
										}`}
										style={{
											borderRadius: '15px',
											minHeight: '80px',
											backgroundColor: isChecked
												? `${argonTheme.COLORS.INFO}15`
												: argonTheme.COLORS.WHITE,
											borderColor: isChecked
												? argonTheme.COLORS.INFO
												: argonTheme.COLORS.BORDER,
											borderWidth: isChecked ? '2px' : '1px',
											transition: 'all 0.2s ease',
											cursor: isDisabled ? 'not-allowed' : 'pointer',
										}}
										disabled={isDisabled}
										onClick={() =>
											!isDisabled && handleCheckboxChange(option.value)
										}>
										<Form.Check
											type='checkbox'
											className='me-3'
											style={{ transform: 'scale(1.3)' }}
											checked={isChecked}
											onChange={() => {}}
											disabled={isDisabled}
											label=''
											id={`checkbox-${data?.id}-${idx}`}
										/>

										{option.imageUrl && (
											<div
												className='me-3'
												style={{ width: '60px', height: '60px' }}>
												<Image
													src={option.imageUrl}
													alt={option.label}
													className='img-fluid h-100 w-100'
													style={{ objectFit: 'contain' }}
												/>
											</div>
										)}

										<div className='text-start flex-grow-1'>
											<p
												className='mb-0'
												style={{
													color: argonTheme.COLORS.DEFAULT,
													fontWeight: isChecked ? '600' : '400',
													fontSize: '0.95rem',
													lineHeight: '1.3',
													display: '-webkit-box',
													WebkitLineClamp: 3,
													WebkitBoxOrient: 'vertical',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
												}}>
												{option.label}
											</p>

											{/* {option.description && (
												<small
													className='text-muted d-block mt-1'
													style={{ whiteSpace: 'pre-wrap' }}>
													{option.description}
												</small>
											)} */}
										</div>
										{!!option?.lst_price && (
											<span className='text-center mt-2 small text-muted'>
												{option?.lst_price} €
											</span>
										)}
									</Button>
								</Col>
							);
						})}
					</Row>
				) : (
					<div className='text-center py-5'>
						<p className='text-muted mb-0'>Aucune option disponible</p>
					</div>
				)}
			</Card.Body>

			{data?.description && (
				<Card.Footer className='border-0 bg-transparent mt-3'>
					<p className='text-muted mb-0 small'>{data.description}</p>
				</Card.Footer>
			)}
		</Card>
	);
};

CheckBoxInput.propTypes = {
	data: PropTypes.shape({
		id: PropTypes.string,
		label: PropTypes.string,
		required: PropTypes.bool,
		description: PropTypes.string,
		options: PropTypes.arrayOf(
			PropTypes.shape({
				value: PropTypes.any.isRequired,
				label: PropTypes.string.isRequired,
				checked: PropTypes.bool,
				disabled: PropTypes.bool,
				description: PropTypes.string,
				imageUrl: PropTypes.string,
				nextStep: PropTypes.any,
			}),
		),
		nextStepData: PropTypes.any,
		style: PropTypes.object,
	}).isRequired,
	handelChange: PropTypes.func.isRequired,
	currentFormValue: PropTypes.object,
};

CheckBoxInput.defaultProps = {
	currentFormValue: {},
};

export default CheckBoxInput;
